import axios from 'axios';
import { AuthResponse, StudentRegistration, AgentRequest, AgentResponse } from '../types';

// Use /api prefix - works with both dev proxy and production nginx
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Public pages that don't require auth - don't redirect from these
const PUBLIC_PATHS = ['/', '/login', '/about', '/privacy', '/terms', '/contact', '/landing-old', '/landing-v3'];

// Token refresh state — prevents concurrent refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function clearAuth() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('student_id');
}

// Add response interceptor with silent token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip refresh logic for the refresh endpoint itself to avoid infinite loop
    if (originalRequest?.url === '/auth/refresh') {
      clearAuth();
      return Promise.reject(error);
    }

    // Account-ban short-circuit: server returns 403 with detail.code='account_banned'.
    // Redirect once to /banned (skip if we're already there to avoid loops).
    const banDetail = error.response?.status === 403 ? error.response?.data?.detail : null;
    if (banDetail && typeof banDetail === 'object' && banDetail.code === 'account_banned') {
      try {
        sessionStorage.setItem('lomi_ban_detail', JSON.stringify(banDetail));
      } catch {}
      if (window.location.pathname !== '/banned') {
        window.location.href = '/banned';
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        // No refresh token — clear and redirect
        clearAuth();
        const currentPath = window.location.pathname;
        if (!PUBLIC_PATHS.includes(currentPath)) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Another refresh is in progress — queue this request
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            originalRequest._retry = true;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;
      originalRequest._retry = true;

      try {
        // Use raw axios to avoid interceptor recursion
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const newAccessToken = response.data.access_token;
        localStorage.setItem('access_token', newAccessToken);

        // Replay queued requests
        onRefreshed(newAccessToken);

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed — clear everything and redirect
        clearAuth();
        refreshSubscribers = [];
        const currentPath = window.location.pathname;
        if (!PUBLIC_PATHS.includes(currentPath)) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  sendOTP: async (phoneNumber: string): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/send-otp', {
      phone_number: phoneNumber,
    });
    return response.data;
  },

  verifyOTP: async (phoneNumber: string, verificationCode: string): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/verify-otp', {
      phone_number: phoneNumber,
      verification_code: verificationCode,
    });

    // Store tokens if successful
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('student_id', response.data.student_id || '');
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
    }

    return response.data;
  },

  register: async (registration: StudentRegistration) => {
    const response = await api.post('/auth/register', registration);
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('student_id');
  },
};

// Telegram Login API
export interface TelegramAuthData {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export interface TelegramAuthResponse {
  success: boolean;
  token?: string;
  refresh_token?: string;
  is_new_user: boolean;
  student_id?: string;
  first_name?: string;
  message?: string;
}

export type TelegramWidgetEvent =
  | 'widget_mounted'
  | 'widget_script_error'
  | 'callback_received'
  | 'api_post_started'
  | 'api_post_failed';

export function logTelegramWidgetEvent(
  event: TelegramWidgetEvent,
  extra: {
    telegram_id?: number;
    telegram_username?: string;
    error_status?: number;
    error_message?: string;
  } = {}
): void {
  try {
    const url = `${API_BASE_URL}/auth/telegram/telemetry`;
    const body = JSON.stringify({
      event,
      hostname: window.location.hostname,
      ...extra,
    });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    // Fallback: fetch with keepalive so it survives navigation
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Telemetry must never throw
  }
}

export const telegramAuthAPI = {
  login: async (authData: TelegramAuthData): Promise<TelegramAuthResponse> => {
    const response = await api.post<TelegramAuthResponse>('/auth/telegram/login', authData);

    // Store tokens if successful
    if (response.data.success && response.data.token) {
      localStorage.setItem('access_token', response.data.token);
      localStorage.setItem('student_id', response.data.student_id || '');
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
    }

    return response.data;
  },

  getBotUsername: async (): Promise<{ username: string; widget_script: string }> => {
    const response = await api.get('/auth/telegram/bot-username');
    return response.data;
  },
};

// Phone waitlist API (used during the v1 launch window before Afromessage SMS is live)
export interface WaitlistResponse {
  success: boolean;
  message: string;
  expected_launch_date: string;
  already_on_list: boolean;
}

export const phoneWaitlistAPI = {
  join: async (
    phoneNumber: string,
    countryCode?: string,
    source: string = 'web'
  ): Promise<WaitlistResponse> => {
    const response = await api.post<WaitlistResponse>('/auth/phone/waitlist', {
      phone_number: phoneNumber,
      country_code: countryCode,
      source,
    });
    return response.data;
  },
};

export interface LinkTokenResponse {
  token: string;
  deep_link_url: string;
  expires_in_seconds: number;
}

export const accountLinkAPI = {
  // Request a one-time Telegram link token. Returns a deep_link_url the
  // user should open in a new tab (t.me/<bot>?start=link_<token>).
  requestToken: async (): Promise<LinkTokenResponse> => {
    const response = await api.post<LinkTokenResponse>(
      '/account/link/request-token',
    );
    return response.data;
  },
};

export const studentAPI = {
  getProfile: async () => {
    const response = await api.get('/student/profile');
    return response.data;
  },

  updateSubjects: async (subjects: string[]) => {
    const response = await api.put('/student/subjects', { subjects });
    return response.data;
  },

  updateProfile: async (fields: {
    first_name?: string;
    last_name?: string;
    profile_name?: string;
    school_name?: string;
    stream?: 'natural' | 'social';
  }) => {
    const response = await api.put('/student/profile', fields);
    return response.data;
  },

  uploadAvatar: async (file: File): Promise<{
    avatar_url: string | null;
    avatar_source: string | null;
    avatar_updated_at: string | null;
  }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/student/profile/avatar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  removeAvatar: async () => {
    const response = await api.delete('/student/profile/avatar');
    return response.data;
  },

  updateLanguagePreference: async (language: string) => {
    const response = await api.put('/student/preferred-language', {
      preferred_language: language,
    });
    return response.data;
  },
};

export interface ContentReportRequest {
  surface: 'web' | 'bot';
  entity_type: 'question' | 'module_component' | 'explanation' | 'lomi_message' | 'key_term' | 'other';
  entity_id: string;
  sub_target?: string;
  category: 'wrong_answer' | 'factual_error' | 'formatting' | 'unclear' | 'broken' | 'other';
  note: string;
  location_ref?: string;
}

export const contentReportsAPI = {
  submit: async (body: ContentReportRequest) => {
    const response = await api.post('/content-reports', body);
    return response.data as { success: boolean; report_id?: string; message: string };
  },
};

export interface UserFeedbackRequest {
  surface: 'web' | 'bot' | 'other';
  // 'suggestion' and 'praise' kept for back-compat with historical rows;
  // current Contact us UI surfaces only bug/content/account/other.
  category?: 'bug' | 'content' | 'account' | 'other' | 'suggestion' | 'praise';
  message: string;
  contact?: string;
  location_ref?: string;
}

export const userFeedbackAPI = {
  submit: async (body: UserFeedbackRequest) => {
    const response = await api.post('/user-feedback', body);
    return response.data as { success: boolean; feedback_id?: string };
  },
};

export interface ReferralStats {
  referral_code: string | null;
  count: number;
  recent: Array<{
    first_name: string;
    school_name: string | null;
    joined_at: string | null;
  }>;
}

export const referralAPI = {
  getStats: async (): Promise<ReferralStats> => {
    const response = await api.get('/me/referrals');
    return response.data as ReferralStats;
  },
};

export const agentAPI = {
  makeRequest: async (request: AgentRequest): Promise<AgentResponse> => {
    const response = await api.post<AgentResponse>('/agent/request', request);
    return response.data;
  },

  getProgress: async (subject: string) => {
    const response = await api.get(`/agent/progress/${subject}`);
    return response.data;
  },

  startAssessment: async (subject: string, assessmentType: string = 'practice', topic?: string) => {
    const response = await api.post('/agent/assessment/start', {
      subject,
      assessment_type: assessmentType,
      topic,
    });
    return response.data;
  },
};

export const assessmentAPI = {
  getPublishedAssessments: async (subject?: string) => {
    const params = subject ? `?subject=${encodeURIComponent(subject)}` : '';
    const response = await api.get(`/assessments/published${params}`);
    return response.data;
  },

  getAssessmentQuestions: async (assessmentId: string) => {
    const response = await api.get(`/assessments/${assessmentId}/questions`);
    return response.data;
  },

  submitAssessment: async (assessmentId: string, submissionData: any) => {
    const response = await api.post(`/assessments/${assessmentId}/submit`, submissionData);
    return response.data;
  },
};

export const diagnosticAPI = {
  getStatus: async () => {
    const response = await api.get('/diagnostic/status');
    return response.data;
  },
  start: async (subject: string) => {
    const response = await api.post(`/diagnostic/${encodeURIComponent(subject)}/start`);
    return response.data;
  },
};

export const mcpAPI = {
  listTools: async () => {
    const response = await api.post('/mcp/tools/list');
    return response.data;
  },

  callTool: async (toolName: string, args: Record<string, any>) => {
    const response = await api.post('/mcp/tools/call', {
      tool_name: toolName,
      arguments: args,
    });
    return response.data;
  },
};

/**
 * Package catalog — what a student can prepare for, and what they hold.
 *
 * Distinct from subscriptionAPI: a tier is a recurring plan attached to a
 * person, a package is a one-time purchase of a program. See
 * docs/architecture/package_system.md §3.7 — they stay orthogonal.
 *
 * Returns `{enabled: false, packages: []}` while the catalog switch is off,
 * which is a normal response and not an error.
 */
/** One question as every package surface receives it.
 *
 *  `passage` is the stimulus a question cannot be answered without — a reading
 *  passage, or the statements a deduction reasons from. When it is present the
 *  `question` field is the SHORT stem, with the passage removed: the two are
 *  emitted together and must be rendered together, or the student is asked
 *  "what is the main idea of the passage?" with no passage on screen. */
export type PackageItem = {
  question_id: string;
  question: string;
  options: Array<{ label: string; text: string }>;
  correct_answer: string;
  explanation: string;
  distractor_notes: Record<string, string>;
  topic: string | null;
  category: string | null;
  passage?: string | null;
  /** Stable across every question the stimulus serves, so the runner can tell
   *  "still the same passage" from "a new one". */
  passage_id?: string | null;
  passage_kind?: 'passage' | 'scenario' | null;
  figure?: { svg: string; alt?: string } | null;
};

export type MockAttempt = {
  session_id: string;
  /** Which authored paper this attempt was. Null on attempts recorded before
   *  the mock room existed. */
  form_code: string | null;
  started_at: string | null;
  completed_at: string | null;
  finished: boolean;
  total: number;
  answered: number;
  correct: number;
  score_pct: number | null;
};

/** A paper as the mock room lists it — not the paper itself. */
export type MockPaperCard = {
  form_code: string;
  role: 'QUANT_MOCK' | 'VA_MOCK' | string;
  letter: 'A' | 'B' | string;
  label: string;
  icon: string;
  blurb: string;
  minutes: number;
  count: number;
  /** The second form of the same product — the re-test after review. */
  is_retest: boolean;
  status: 'not_started' | 'started' | 'done';
  attempt: MockAttempt | null;
  locked: boolean;
  locked_reason: 'access' | 'retest' | null;
};

export type MockPaper = {
  mode: 'mock';
  form_code: string;
  role: string;
  letter: string;
  label: string;
  icon: string;
  sections: Array<{
    key: string;
    label: string;
    count: number;
    minutes: number;
    blueprint_items: number;
    blueprint_minutes: number;
    pool: number;
    /** Verbal 35 / Analytical 20, for the report. NOT section clocks — the
     *  paper is one clock and a student may work back and forth across it. */
    parts: Array<{ label: string; count: number }>;
    items: PackageItem[];
  }>;
  total: number;
  minutes: number;
  ceiling: number;
  subjects: string[];
  is_representative: boolean;
  authored: true;
};

export const packagesAPI = {
  list: async () => {
    const response = await api.get('/student/packages');
    return response.data as {
      enabled: boolean;
      packages: Array<{
        key: string;
        name: string;
        tagline: string | null;
        kind: string;
        pricing: { kind?: string; amount?: number; currency?: string; note?: string };
        free: boolean;
        enrollment_schema: Array<Record<string, any>>;
        subjects: string[];
        programs: Array<{ program_id: string; program_code: string }>;
        held: boolean;
        enrolled: boolean;
        access: boolean;
        cta: 'continue' | 'start' | 'buy';
        /** Where Start/Continue lands. Declared per program in its manifest;
         *  defaults to /dashboard for the ESSLCE-shaped packages the existing
         *  dashboard genuinely serves. */
        home: string;
      }>;
    };
  },

  /**
   * Pick a package. `answers` satisfies whatever the program declared in
   * `enrollment_schema` — `{stream: 'natural'}` for ESSLCE, `{}` for UAT.
   *
   * Free packages come back `next: 'start'` with access already granted; paid
   * ones come back `next: 'checkout'` holding a pending entitlement that
   * unlocks nothing. A 400 carries a message written to show the student.
   */
  select: async (cardKey: string, answers: Record<string, string> = {}) => {
    const response = await api.post(
      `/student/packages/${encodeURIComponent(cardKey)}/select`,
      { answers }
    );
    return response.data as {
      next: 'start' | 'checkout';
      program_code: string;
      selection: Record<string, string>;
      pricing: { kind?: string; amount?: number; currency?: string; note?: string };
    };
  },

  /** What is owed and whether payment is open. `mode` is off | stub | live. */
  checkout: async (cardKey: string) => {
    const response = await api.get(
      `/student/packages/${encodeURIComponent(cardKey)}/checkout`
    );
    return response.data as {
      package: { key: string; name: string; tagline: string | null; home: string };
      amount: number | null;
      currency: string;
      note: string | null;
      status: 'none' | 'pending' | 'active' | 'expired' | 'revoked';
      settled: boolean;
      mode: 'off' | 'stub' | 'live' | 'manual';
      /** Present only when mode is 'manual': where to pay, and whether this
       *  student already has a receipt with us. */
      manual?: {
        bank: Record<string, any>;
        instructions: { title?: string; steps?: string[]; note?: string } | null;
        request: {
          request_id: string;
          /** No 'submitted' here — the DB constraint has no such status. Use
           *  the server-computed `submitted` flag below instead. */
          status: 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled';
          payment_reference: string | null;
          rejection_reason: string | null;
          created_at: string | null;
          has_proof: boolean;
          /** The receipt is with us and waiting on a person. */
          submitted: boolean;
        } | null;
      };
    };
  },

  /** Should this student be shown the UAT prompt, and in what words?
   *  The copy lives in settings so it can be corrected from the admin
   *  dashboard; `show` is false unless the launch switch says otherwise. */
  /**
   * Should EAU appear on this student's dashboard, and in what words.
   *
   * Its own endpoint, reading EAU's own switches — nothing here is shared
   * with UAT beyond the shape of the answer.
   */
  eauPrompt: async () => {
    const response = await api.get('/student/packages/eau/prompt');
    return response.data as {
      show: boolean;
      held?: boolean;
      prompt: { title?: string; body?: string; cta?: string; dismiss?: string };
      amount: number | null;
      currency: string;
      home?: string;
    };
  },

  uatPrompt: async () => {
    const response = await api.get('/student/packages/uat/prompt');
    return response.data as {
      show: boolean;
      prompt: { title?: string; body?: string; cta?: string; dismiss?: string };
      amount: number | null;
      currency: string;
    };
  },

  /** Bank-transfer payment for a package. Idempotent: calling it twice returns
   *  the SAME request rather than a second row for one transfer. */
  startBankPayment: async (programCode: string) => {
    const response = await api.post('/payment/manual/initiate-package', {
      program_code: programCode,
    });
    return response.data as {
      request_id: string;
      amount: number;
      currency: string;
      bank_details: Record<string, any>;
      instructions: any;
    };
  },

  /** Attach the receipt. `file` is whatever the student picked or photographed. */
  uploadPaymentProof: async (requestId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(
      `/payment/manual/upload-proof/${encodeURIComponent(requestId)}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /** Hand it to the admin queue. After this the student waits.
   *  `payment_reference` is optional — the uploaded receipt is the evidence. */
  submitPayment: async (
    requestId: string,
    payload: { payment_reference?: string; payment_date?: string; notes?: string }
  ) => {
    const response = await api.post(
      `/payment/manual/submit/${encodeURIComponent(requestId)}`,
      payload
    );
    return response.data;
  },

  /** Lessons in a package. Same payload feeds all three delivery shells. */
  lessons: async (cardKey: string) => {
    const response = await api.get(
      `/student/packages/${encodeURIComponent(cardKey)}/lessons`
    );
    return response.data as {
      package: string;
      lessons: Array<{
        module_id: string; title: string; topic: string; subtopic: string;
        minutes: number; kind: string; slug: string;
        objectives: string[]; components: number;
      }>;
      /** The server's verdict on this student: 'full' | 'free'. The shell
       *  reads it rather than inferring access from locked lessons, so an
       *  admin grant silences every trial line in one place. */
      access: string;
    };
  },

  /** One lesson, grouped into phases, carrying the declared `kind`. */
  lesson: async (cardKey: string, slug: string) => {
    const response = await api.get(
      `/student/packages/${encodeURIComponent(cardKey)}/lessons/${encodeURIComponent(slug)}`
    );
    return response.data;
  },

  /** Which question pool backs this package's practice surface. */
  practice: async (cardKey: string) => {
    const response = await api.get(
      `/student/packages/${encodeURIComponent(cardKey)}/practice`
    );
    return response.data as {
      subjects: Array<{ subject: string; questions: number }>;
    };
  },

  /**
   * A timed, section-locked sitting — the readiness check or the full mock.
   *
   * Sections arrive in order, each with its own clock and its items already
   * hydrated: a timed paper must not spend its opening seconds fetching.
   *
   * `is_representative` is false whenever the pool could not fill the
   * blueprint. The runner must not call such a sitting a mock — a score off
   * 16 items is an estimate of an estimate, and saying so is the difference
   * between a useful signal and a misleading one.
   */
  sitting: async (cardKey: string, full = false) => {
    const response = await api.get(
      `/student/packages/${encodeURIComponent(cardKey)}/sitting`,
      { params: { full } }
    );
    return response.data as {
      sections: Array<{
        key: string; label: string; count: number; minutes: number;
        blueprint_items: number; blueprint_minutes: number; pool: number;
        /** Only an authored paper carries parts; declared here so the runner
         *  can read one section shape across both sources. */
        parts?: Array<{ label: string; count: number }>;
        items: PackageItem[];
      }>;
      total: number;
      minutes: number;
      mode: 'readiness' | 'full';
      ceiling: number;
      subjects: string[];
      is_representative: boolean;
      reason?: string;
    };
  },

  /**
   * One AUTHORED mock paper, whole and in its authored order.
   *
   * Not to be confused with `sitting` above, which samples the practice pool.
   * This is a fixed form: the same 45 or 55 items for every student who sits
   * it, which is what makes a score comparable and the second form a re-test
   * rather than a different exam. `is_representative` is true by construction.
   */
  mockPaper: async (cardKey: string, formCode: string) => {
    const response = await api.get(
      `/student/packages/${encodeURIComponent(cardKey)}/mocks/${encodeURIComponent(formCode)}`
    );
    return response.data as MockPaper;
  },

  /** The mock room: the papers on offer with their status, and every mock this
   *  student has sat, newest first. Abandoned attempts are included on purpose
   *  — walking out halfway is a result too. */
  mocks: async (cardKey: string) => {
    const response = await api.get(
      `/student/packages/${encodeURIComponent(cardKey)}/mocks`
    );
    return response.data as {
      attempts: MockAttempt[];
      papers: MockPaperCard[];
      access: 'free' | 'full';
      /** The free checks THIS package declares. Empty is meaningful: it means
       *  the package has none, not that it should fall back to another's. */
      checks?: { subject: string; label: string; icon?: string }[];
    };
  },

  /** What the student has done in this package. Rank is cohort-gated. */
  progress: async (cardKey: string) => {
    const response = await api.get(
      `/student/packages/${encodeURIComponent(cardKey)}/progress`
    );
    return response.data as {
      answered: number; correct: number; accuracy: number | null;
      misses_due: number; cohort: number;
      rank: number | null; rank_hidden: string | null;
    };
  },

  /** Settle via the stub provider. Only works while mode is 'stub'. */
  confirmCheckout: async (cardKey: string) => {
    const response = await api.post(
      `/student/packages/${encodeURIComponent(cardKey)}/checkout/confirm`
    );
    return response.data as {
      settled: boolean;
      already: boolean;
      program_code: string;
    };
  },
};

/**
 * The post-run "Victory Moment" numbers — XP, rank movement, combo, streak.
 *
 * This already existed and was already rich; what it lacked was callers. Any
 * surface that finishes a session should show it, or the student does the work
 * and is told nothing about what it earned them.
 */
export const gamificationAPI = {
  sessionSummary: async (sessionId: string) => {
    const response = await api.get(`/gamification/session-summary/${sessionId}`);
    return response.data as {
      xp_earned: number;
      rank: { rank: number; xp: number; total_ranked: number } | null;
      rank_before: { rank: number; xp: number; total_ranked: number } | null;
      streak: { current: number; longest: number; alive: boolean };
      combo: { session_max: number; lifetime_max: number; is_personal_best: boolean };
      session_xp_pb: { is_personal_best: boolean; prev_best: number };
      personal_bests: Array<{
        kind: 'combo' | 'session_xp';
        label: string;
        old: number;
        new: number;
        unit: string;
      }>;
    };
  },
};

/**
 * The module ENGINE, shared with ESSLCE.
 *
 * These are the exact endpoints the ESSLCE lessons tab drives, and they are
 * subject-agnostic: every one is keyed by module_id + student_id and mentions
 * no subject or stream. So a package gets sequencing, resume, completion and
 * scoring by CALLING what already works for 26,234 students, rather than
 * reimplementing it — while keeping its own presentation.
 *
 * `progress.current_component_index` is the resume point. That single field is
 * the difference between a lesson being a document and being a course.
 */
export const moduleEngineAPI = {
  session: async (
    moduleId: string,
    studentId: string,
    opts: { restart?: boolean; review?: boolean } = {}
  ) => {
    const response = await api.get(
      `/micro-learning/modules/${encodeURIComponent(moduleId)}/session`,
      { params: { student_id: studentId, restart: opts.restart, review: opts.review } }
    );
    return response.data as {
      module: Record<string, any>;
      current_component: Record<string, any> | null;
      progress: {
        current_component_index: number;
        total_components: number;
        completion_percentage: number;
        status?: string;
        correct_answers?: number;
        total_questions?: number;
      };
      component_list?: Array<{
        component_id: string;
        component_type: string;
        title?: string;
        student_response?: any;
      }>;
      section_completion?: Record<string, boolean>;
      review_mode?: boolean;
    };
  },

  /** Record an answer / mark a component done, and advance the engine. */
  respond: async (
    moduleId: string,
    componentId: string,
    studentId: string,
    responseData: Record<string, any>,
    timeSpentSeconds = 0
  ) => {
    const response = await api.post(
      `/micro-learning/modules/${encodeURIComponent(moduleId)}/components/${encodeURIComponent(componentId)}/response`,
      { response_data: responseData, time_spent_seconds: timeSpentSeconds },
      { params: { student_id: studentId } }
    );
    return response.data;
  },

  /** Move to any component by index — how a student goes back. */
  jump: async (moduleId: string, studentId: string, componentIndex: number) => {
    const response = await api.post(
      `/micro-learning/modules/${encodeURIComponent(moduleId)}/jump-to-component`,
      null,
      { params: { student_id: studentId, component_index: componentIndex } }
    );
    return response.data;
  },

  complete: async (moduleId: string, studentId: string) => {
    const response = await api.post(
      `/micro-learning/modules/${encodeURIComponent(moduleId)}/complete`,
      null,
      { params: { student_id: studentId } }
    );
    return response.data;
  },
};

export const subscriptionAPI = {
  // Get available tiers
  getTiers: async () => {
    const response = await api.get('/subscription/tiers');
    return response.data;
  },

  // Get current subscription with usage
  getCurrentSubscription: async () => {
    const response = await api.get('/subscription/current');
    return response.data;
  },

  // Get detailed usage
  getUsageDetails: async () => {
    const response = await api.get('/subscription/usage');
    return response.data;
  },

  // Add member to subscription
  addMember: async (phoneNumber: string) => {
    const response = await api.post('/subscription/members/add', null, {
      params: { phone_number: phoneNumber }
    });
    return response.data;
  },

  // Remove member from subscription
  removeMember: async (memberId: string) => {
    const response = await api.delete(`/subscription/members/${memberId}`);
    return response.data;
  },

  // Check feature access
  checkFeatureAccess: async (featureName: string) => {
    const response = await api.post('/subscription/check-feature', null, {
      params: { feature_name: featureName }
    });
    return response.data;
  },

  // Start trial subscription
  startTrial: async (tierId: string) => {
    const response = await api.post('/subscription/start-trial', {
      tier_id: tierId
    });
    return response.data;
  },

  // Assign free tier to new student
  assignFreeTier: async () => {
    const response = await api.post('/subscription/assign-free-tier');
    return response.data;
  },

  // Get trial status
  getTrialStatus: async () => {
    const response = await api.get('/subscription/trial-status');
    return response.data;
  },

  // Convert trial to paid subscription
  convertTrial: async (paymentMethod: string, durationMonths: number) => {
    const response = await api.post('/subscription/convert-trial', {
      payment_method: paymentMethod,
      duration_months: durationMonths
    });
    return response.data;
  },
};

// AI Tutor API - Ask Lomi

// Tutor mode controls what context the AI sees
export type TutorMode = 'learn' | 'revision' | 'assessment' | 'speed';

export interface AskLomiRequest {
  question: string;
  context_type: 'module_session' | 'eslce_quiz' | 'dashboard' | 'general';
  module_id?: string;
  component_id?: string;
  question_id?: string;
  conversation_id?: string;

  // Entry-point surface (e.g. "web_past_exam", "lesson_mcq", "home").
  source?: string;

  // How the turn was entered: 'voice' when it came from the mic transcribe
  // path, 'text' (default) otherwise. Persisted on the trace for analytics.
  input_method?: 'text' | 'voice';

  // Tutor mode - controls what context is visible to the AI
  mode?: TutorMode;

  // Session context for better AI responses
  time_on_component_seconds?: number;
  last_answer?: string;
  last_answer_correct?: boolean;
  consecutive_wrong?: number;
  session_questions_attempted?: number;
  session_questions_correct?: number;

  // For "Generate Similar" feature - original component to base generation on
  source_component?: {
    type: string;
    content: Record<string, any>;
  };

  // Aptitude daily drill — when set, the prompt builder gets one line
  // about which day of the 45-day path and what strategy is being trained.
  // Optional; backend tolerates the fields being absent.
  drill_day_index?: number;
  drill_strategy_title?: string;
}

// Interactive component types for chat
export interface InteractiveComponent {
  component_id: string;
  component_type: 'mcq' | 'flashcard' | 'true_false';
  data: any;
  source: string;
}

export interface SubmitAnswerRequest {
  component_id: string;
  answer: string;
  conversation_id?: string;
  response_time_ms?: number;
}

export interface SubmitAnswerResponse {
  success: boolean;
  is_correct: boolean;
  correct_answer: string;
  explanation?: string;
  follow_up_prompts: Array<{
    label: string;
    prompt: string;
    category: string;
  }>;
}

export interface AskLomiResponse {
  success: boolean;
  content: string;
  follow_up_prompts: Array<{
    label: string;
    prompt: string;
    category: string;
  }>;
  sources: Array<{
    type: string;
    id: string;
    [key: string]: any;
  }>;
  interactive_component?: InteractiveComponent;
  conversation_id?: string;
  response_time_ms?: number;
  usage?: {
    messages_used: number;
    messages_limit: number;
    messages_remaining: number;
    percentage_used: number;
    tier: string;
    can_send: boolean;
  };
}

export interface AIUsageStatus {
  messages_used: number;
  messages_limit: number;
  messages_remaining: number;
  percentage_used: number;
  tier: string;
  can_send: boolean;
}

// Component trigger from database configuration
export interface ComponentTrigger {
  rule_id: string;
  label: string;
  prompt: string;
  category: 'clarify' | 'connect' | 'practice' | 'deepen' | 'trap';
  priority: 'high' | 'medium' | 'low';
  trigger_location: 'chat_response' | 'component_inline' | 'result_card' | 'definition_item' | 'content_footer';
  context_hint?: string;
}

// Component triggers are static UI config that re-fetches on every component
// mount. Cache per (componentType|triggerLocation|subject) — matching the
// useEffect deps in ComponentTriggers.tsx, which already ignore `context` for
// refetching — so repeated mounts of the same component hit memory, not the
// network. Stores the in-flight promise to also dedupe concurrent mounts.
const componentTriggerCache = new Map<string, Promise<ComponentTrigger[]>>();

export const aiTutorAPI = {
  // Ask Lomi a question
  ask: async (request: AskLomiRequest): Promise<AskLomiResponse> => {
    const response = await api.post<AskLomiResponse>('/ai-tutor/ask', request);
    return response.data;
  },

  // Get current AI usage status
  getUsage: async (): Promise<AIUsageStatus> => {
    const response = await api.get<AIUsageStatus>('/ai-tutor/usage');
    return response.data;
  },

  // Get conversation history
  getConversations: async (subject?: string, limit: number = 10) => {
    const params = new URLSearchParams();
    if (subject) params.append('subject', subject);
    params.append('limit', limit.toString());
    const response = await api.get(`/ai-tutor/conversations?${params.toString()}`);
    return response.data;
  },

  // Get a specific conversation
  getConversation: async (conversationId: string) => {
    const response = await api.get(`/ai-tutor/conversations/${conversationId}`);
    return response.data;
  },

  // Health check
  health: async () => {
    const response = await api.get('/ai-tutor/health');
    return response.data;
  },

  // Submit answer for interactive component (MCQ, True/False, Flashcard)
  submitAnswer: async (request: SubmitAnswerRequest): Promise<SubmitAnswerResponse> => {
    const response = await api.post<SubmitAnswerResponse>('/ai-tutor/submit-answer', request);
    return response.data;
  },

  // Generate a similar question based on a module component
  generateSimilarQuestion: async (
    moduleId: string,
    componentId: string,
    componentType: string,
    contentData: Record<string, any>
  ): Promise<AskLomiResponse> => {
    const request: AskLomiRequest = {
      question: "Generate a similar practice question",
      context_type: 'module_session',
      module_id: moduleId,
      component_id: componentId,
      source_component: {
        type: componentType,
        content: contentData
      }
    };
    const response = await api.post<AskLomiResponse>('/ai-tutor/ask', request);
    return response.data;
  },

  // Get component triggers for a specific component type and location
  getComponentTriggers: async (
    componentType: string,
    triggerLocation: string,
    subject?: string,
    context?: Record<string, any>
  ): Promise<ComponentTrigger[]> => {
    const cacheKey = `${componentType}|${triggerLocation}|${subject ?? ''}`;
    const cached = componentTriggerCache.get(cacheKey);
    if (cached) return cached;

    const fetchPromise = api
      .post<ComponentTrigger[]>('/ai-tutor/component-triggers', {
        component_type: componentType,
        trigger_location: triggerLocation,
        subject: subject,
        context: context
      })
      .then((response) => response.data)
      .catch((err) => {
        // Don't cache failures — let the next mount retry (and fall back to
        // hardcoded triggers in the meantime).
        componentTriggerCache.delete(cacheKey);
        throw err;
      });

    componentTriggerCache.set(cacheKey, fetchPromise);
    return fetchPromise;
  },

  // Track when a trigger is clicked (for analytics)
  trackTriggerClick: async (ruleId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/ai-tutor/trigger-click', {
      rule_id: ruleId
    });
    return response.data;
  },

  // Transcribe audio to text using Whisper
  transcribe: async (audioBlob: Blob): Promise<{ success: boolean; text: string; language: string }> => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    const response = await api.post('/ai-tutor/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Synthesize text to speech using OpenAI TTS
  synthesize: async (text: string, voice: string = 'nova'): Promise<Blob> => {
    const response = await api.post('/ai-tutor/synthesize',
      { text, voice },
      { responseType: 'arraybuffer' }
    );
    return new Blob([response.data], { type: 'audio/mpeg' });
  },

  // Translate an AI response into Amharic via Gemini. `contentType` (e.g.
  // 'key_term') selects a tailored prompt server-side and namespaces the cache.
  translateAmharic: async (text: string, contentType?: string): Promise<{ success: boolean; amharic_text: string; model: string }> => {
    const body: { text: string; content_type?: string } = { text };
    if (contentType) body.content_type = contentType;
    const response = await api.post('/ai-tutor/translate-am', body);
    return response.data;
  },
};

export const paymentAPI = {
  // Get payment configuration
  getConfig: async () => {
    const response = await api.get('/payment/config');
    return response.data;
  },

  // Calculate subscription price
  calculatePrice: async (tierId: string, durationMonths: number, additionalStudents: number) => {
    const response = await api.post('/payment/calculate-price', {
      tier_id: tierId,
      duration_months: durationMonths,
      additional_students: additionalStudents
    });
    return response.data;
  },

  // Initiate payment
  initiatePayment: async (tierId: string, durationMonths: number, additionalStudents: number) => {
    const response = await api.post('/payment/initiate', {
      tier_id: tierId,
      duration_months: durationMonths,
      additional_students: additionalStudents
    });
    return response.data;
  },

  // Verify payment status
  verifyPayment: async (transactionId: string) => {
    const response = await api.get(`/payment/verify/${transactionId}`);
    return response.data;
  },

  // Get payment history
  getPaymentHistory: async () => {
    const response = await api.get('/payment/history');
    return response.data;
  },
};

// Past Exam Practice API - Session persistence for ESLCE practice
export const pastExamAPI = {
  startSession: async (data: {
    student_id: string;
    subject: string;
    topic?: string;
    year?: number;
    question_ids: string[];
    /** Which surface produced this run — "sitting", "lesson", "questions".
     *  Lands in session_metadata so the surfaces can be told apart later;
     *  without it every session looks like ordinary practice. */
    origin?: string;
    /** Which authored mock paper this run IS — "QUANT_MOCK_A". `origin` says a
     *  mock happened; this says which one, and the mock room needs it to put a
     *  score on a card and to open the re-test. */
    form_code?: string;
  }) => {
    const response = await api.post('/past-exams/session/start', data);
    return response.data;
  },

  saveAnswer: async (sessionId: string, data: {
    student_id: string;
    question_id: string;
    selected_answer: string;
    is_correct: boolean;
    time_spent_seconds: number;
    topic?: string;
    is_review?: boolean;
  }) => {
    const response = await api.post(`/past-exams/session/${sessionId}/answer`, data);
    return response.data;
  },

  // Append a fresh batch to an in-progress session so the consecutive-correct
  // combo continues across batches (combo is computed per session_id).
  extendSession: async (sessionId: string, data: {
    student_id: string;
    question_ids: string[];
  }) => {
    const response = await api.post(`/past-exams/session/${sessionId}/extend`, data);
    return response.data;
  },

  getActiveSession: async (studentId: string, subject: string) => {
    const response = await api.get('/past-exams/session/active', {
      params: { student_id: studentId, subject }
    });
    return response.data;
  },

  getStats: async (studentId: string, subject: string) => {
    const response = await api.get('/past-exams/stats', {
      params: { student_id: studentId, subject }
    });
    return response.data;
  },

  completeSession: async (sessionId: string, studentId: string) => {
    const response = await api.post(`/past-exams/session/${sessionId}/complete`, null, {
      params: { student_id: studentId }
    });
    return response.data;
  },

  getQuestionsByIds: async (questionIds: string[], includeMedia: boolean = true) => {
    const response = await api.post('/questions/by-ids', {
      question_ids: questionIds,
      include_media: includeMedia,
    });
    return response.data;
  },
};

// Session Tracking API
export type EventType =
  | 'app_open'
  | 'app_background'
  | 'app_foreground'
  | 'screen_view'
  | 'component_view'
  | 'component_exit'
  | 'scroll'
  | 'interaction'
  | 'answer_start'
  | 'answer_change'
  | 'answer_submit'
  | 'help_request'
  | 'idle_start'
  | 'idle_end'
  | 'module_start'
  | 'module_complete';

export interface SessionEvent {
  event_type: EventType;
  timestamp?: string;
  screen?: string;
  module_id?: string;
  component_id?: string;
  component_type?: string;
  component_order?: number;
  metadata?: Record<string, any>;
}

export interface SessionStartRequest {
  device_type?: 'mobile' | 'tablet' | 'desktop';
  app_version?: string;
  platform?: 'ios' | 'android' | 'web';
  metadata?: Record<string, any>;
}

export interface SessionSummary {
  session_id: string;
  started_at: string;
  ended_at?: string;
  duration_seconds: number;
  is_active: boolean;
  total_events: number;
  screens_viewed: number;
  components_viewed: number;
  questions_attempted: number;
  questions_correct: number;
  accuracy: number;
  engagement_score?: number;
  final_state?: string;
}

export const sessionAPI = {
  // Start a new session
  startSession: async (request: SessionStartRequest = {}): Promise<{ session_id: string; started_at: string }> => {
    // Detect device type
    const deviceType = window.innerWidth <= 768 ? 'mobile' :
                       window.innerWidth <= 1024 ? 'tablet' : 'desktop';

    const response = await api.post('/session/start', {
      device_type: request.device_type || deviceType,
      platform: request.platform || 'web',
      app_version: request.app_version || '1.0.0',
      metadata: request.metadata || {},
    });
    return response.data;
  },

  // Record a single event
  recordEvent: async (event: SessionEvent): Promise<{ status: string; event_id: string }> => {
    const response = await api.post('/session/event', {
      ...event,
      timestamp: event.timestamp || new Date().toISOString(),
    });
    return response.data;
  },

  // Record multiple events at once
  recordEvents: async (events: SessionEvent[]): Promise<{ status: string; events_created: number }> => {
    const response = await api.post('/session/events', {
      events: events.map(e => ({
        ...e,
        timestamp: e.timestamp || new Date().toISOString(),
      })),
    });
    return response.data;
  },

  // End the current session
  endSession: async (metadata: Record<string, any> = {}): Promise<{
    status: string;
    session_id: string;
    duration_seconds: number;
    engagement_score: number;
    final_state: string;
  }> => {
    const response = await api.post('/session/end', { metadata });
    return response.data;
  },

  // Get current active session
  getCurrentSession: async (): Promise<SessionSummary | null> => {
    const response = await api.get('/session/current');
    return response.data;
  },

  // Get session by ID
  getSession: async (sessionId: string): Promise<SessionSummary> => {
    const response = await api.get(`/session/${sessionId}`);
    return response.data;
  },

  // Get session events
  getSessionEvents: async (sessionId: string, options: {
    limit?: number;
    offset?: number;
    event_type?: string;
  } = {}): Promise<{ session_id: string; total: number; events: any[] }> => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.event_type) params.append('event_type', options.event_type);

    const response = await api.get(`/session/${sessionId}/events?${params.toString()}`);
    return response.data;
  },
};

export const studyCoachAPI = {
  getInsight: async (subject: string, location: string = 'dashboard'): Promise<any> => {
    const response = await api.get('/study-coach/insight', {
      params: { subject, location },
    });
    return response.data;
  },

  invalidateCache: async (subject?: string): Promise<any> => {
    const response = await api.post('/study-coach/invalidate-cache', null, {
      params: { subject },
    });
    return response.data;
  },
};

export const studyReportAPI = {
  getReport: async (): Promise<any> => {
    const response = await api.get('/study-report');
    return response.data;
  },
};

/**
 * Engagement telemetry — buffered tap counter for product decisions.
 *
 * Public API: `track('surface.element.action', { ...props })`. Calls are
 * fire-and-forget; they buffer in memory and flush in batches so 40 clicks
 * become ~4 HTTP POSTs instead of 40. Backend writes to engagement_events.
 *
 * Flush triggers (whichever comes first):
 *   - 10 events queued
 *   - 5 seconds since first queued event
 *   - tab hidden / page hide  (uses fetch keepalive)
 *   - manual flushTrackingNow() (exported for tests)
 *
 * Dedupe: identical (name + props) within 500ms is dropped, so a double-tap
 * doesn't become two rows.
 */
type TrackEvent = { name: string; properties?: Record<string, any>; ts: number };

const TRACK_FLUSH_AT = 10;
// 10s flush aligns with useSessionTracker's batch interval and roughly halves
// /telemetry/events POSTs vs the old 5s. Bursts still flush early via
// TRACK_FLUSH_AT (10 events), and unload flushes via visibilitychange/pagehide,
// so no events are lost — they just travel in fewer, fuller batches.
const TRACK_FLUSH_MS = 10000;
const TRACK_DEDUPE_MS = 500;
const TRACK_MAX_BATCH = 50;

let trackQueue: TrackEvent[] = [];
let trackFlushTimer: ReturnType<typeof setTimeout> | null = null;
let trackLastKey = '';
let trackLastKeyAt = 0;

function trackEndpointURL(): string {
  // Mirror axios baseURL resolution — '/api' in prod, env override otherwise.
  return `${API_BASE_URL}/telemetry/events`;
}

function flushTracking(useKeepalive = false): void {
  if (trackFlushTimer) {
    clearTimeout(trackFlushTimer);
    trackFlushTimer = null;
  }
  if (trackQueue.length === 0) return;
  const events = trackQueue.splice(0, TRACK_MAX_BATCH);
  const body = JSON.stringify({ events });

  if (useKeepalive && typeof fetch !== 'undefined') {
    // Page is unloading — use fetch keepalive so the request survives.
    try {
      const token = localStorage.getItem('access_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      fetch(trackEndpointURL(), { method: 'POST', body, headers, keepalive: true }).catch(() => {});
    } catch {
      /* noop */
    }
  } else {
    api.post('/telemetry/events', { events }).catch(() => {});
  }

  if (trackQueue.length > 0) scheduleTrackFlush();
}

function scheduleTrackFlush(): void {
  if (trackFlushTimer) return;
  trackFlushTimer = setTimeout(() => {
    trackFlushTimer = null;
    flushTracking();
  }, TRACK_FLUSH_MS);
}

export function track(name: string, properties?: Record<string, any>): void {
  try {
    if (!name) return;
    const now = Date.now();
    const key = name + '|' + (properties ? JSON.stringify(properties) : '');
    if (key === trackLastKey && now - trackLastKeyAt < TRACK_DEDUPE_MS) return;
    trackLastKey = key;
    trackLastKeyAt = now;

    trackQueue.push({ name, properties, ts: now });
    if (trackQueue.length >= TRACK_FLUSH_AT) {
      flushTracking();
    } else {
      scheduleTrackFlush();
    }
  } catch {
    /* noop */
  }
}

// Force a flush — exported so tests and code that wants a guaranteed
// write (e.g. logout, share-button) can drain the queue immediately.
export function flushTrackingNow(): void {
  flushTracking();
}

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTracking(true);
  });
  window.addEventListener('pagehide', () => flushTracking(true));
}

export default api;
